import { useState, useEffect } from 'react';
import { Download, X, Share } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const DISMISS_KEY = 'flowupy_install_dismissed';

const STR = {
  tr: {
    title: "FlowUpy'yi Yükle",
    iosA: 'Paylaş', iosB: 'menüsünden', iosC: '"Ana Ekrana Ekle"', iosD: 'seçin.',
    desc: 'Telefonunuza uygulama olarak ekleyin, daha hızlı erişin.', install: 'Uygulamayı Yükle', close: 'Kapat',
  },
  en: {
    title: 'Install FlowUpy',
    iosA: 'Share', iosB: 'menu, select', iosC: '"Add to Home Screen"', iosD: '.',
    desc: 'Add it as an app to your phone for faster access.', install: 'Install App', close: 'Close',
  },
};

export const InstallPrompt = () => {
  const { language } = useLanguage();
  const L = STR[language] || STR.tr;
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    const standalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
    if (standalone) return;
    if (localStorage.getItem(DISMISS_KEY) === '1') return;

    const ua = window.navigator.userAgent.toLowerCase();
    const ios = /iphone|ipad|ipod/.test(ua);
    const isSafari = ios && /safari/.test(ua) && !/crios|fxios/.test(ua);

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', handler);

    if (ios && isSafari) {
      setIsIOS(true);
      setVisible(true);
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const dismiss = () => {
    setVisible(false);
    localStorage.setItem(DISMISS_KEY, '1');
  };

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:max-w-sm z-[100] bg-card border border-border rounded-xl shadow-lg p-4 animate-slide-up" data-testid="install-prompt">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center flex-shrink-0">
          <Download className="w-5 h-5 text-primary-foreground" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm">{L.title}</p>
          {isIOS ? (
            <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1 flex-wrap">
              {L.iosA} <Share className="w-3.5 h-3.5 inline" /> {L.iosB} <span className="font-medium">{L.iosC}</span> {L.iosD}
            </p>
          ) : (
            <p className="text-xs text-muted-foreground mt-1">{L.desc}</p>
          )}
          {!isIOS && (
            <button onClick={install} className="mt-3 px-4 py-2 rounded-lg bg-accent text-accent-foreground text-sm font-semibold hover:opacity-90 transition-opacity" data-testid="install-app-btn">
              {L.install}
            </button>
          )}
        </div>
        <button onClick={dismiss} className="p-1 hover:bg-muted rounded transition-colors flex-shrink-0" data-testid="install-dismiss" aria-label={L.close}>
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};
